/**
 * `useSearch` — server state for keyword + semantic search via TanStack Query.
 *
 * The hook owns NO local fetch effect: it derives a stable query key from the
 * URL-backed {@link SearchParams} and lets TanStack Query handle fetching,
 * caching, and de-duplication. Every request carries a fresh
 * `x-client-request-id` header so server logs can be correlated with the UI.
 */
import {
  type KeywordSearchResponse,
  type SearchFilters,
  type SemanticSearchResponse,
} from "@records-monitor/shared";
import { useQuery } from "@tanstack/react-query";
import { paramsToFilters, type SearchParams } from "../urlState";
import { useIdToken } from "@/auth/AuthProvider";

type ApiSearchMode = "keyword" | "semantic";
export type SearchResponse = KeywordSearchResponse | SemanticSearchResponse;

/**
 * The request body we send. `limit`/`rerank` carry server-side defaults, so we
 * only send what the user actually set — this is the *input* shape of the
 * shared keyword/semantic request schemas.
 */
interface SearchRequestBody {
  q: string;
  filters?: SearchFilters;
  cursor?: string;
}

const API_BASE: string = ((): string => {
  const meta = import.meta as { env?: { VITE_API_BASE_URL?: string } };
  return meta.env?.VITE_API_BASE_URL ?? "/api";
})();

/** Generate a client request id used to correlate UI ↔ server logs. */
export function newClientRequestId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  // reason: fallback only when crypto.randomUUID is unavailable (old jsdom).
  return `cri-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

async function postSearch(
  mode: ApiSearchMode,
  body: SearchRequestBody,
  token: string | null,
): Promise<SearchResponse> {
  const headers = new Headers({
    "content-type": "application/json",
    "x-client-request-id": newClientRequestId(),
  });
  if (token) headers.set("authorization", `Bearer ${token}`);

  const response = await fetch(`${API_BASE}/search/${mode}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`Search failed (${response.status})`);
  }
  return response.json() as Promise<SearchResponse>;
}

export interface UseSearchResult {
  data: SearchResponse | undefined;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  /** True when there is no query to run yet. */
  isIdle: boolean;
}

/**
 * Run a keyword or semantic search for the current URL params. The query only
 * fires when there is a non-empty `q` and the mode is keyword/semantic
 * (the "ask" mode streams via {@link useAgentStream} instead).
 */
export function useSearch(params: SearchParams): UseSearchResult {
  const getToken = useIdToken();
  const q = params.q?.trim() ?? "";
  const mode: ApiSearchMode = params.mode === "semantic" ? "semantic" : "keyword";
  const enabled = q.length > 0 && (params.mode === "keyword" || params.mode === "semantic");
  const filters = paramsToFilters(params);

  const query = useQuery({
    queryKey: ["search", mode, q, filters ?? null, params.cursor ?? null],
    enabled,
    queryFn: async (): Promise<SearchResponse> => {
      const token = await getToken();
      const base = {
        q,
        ...(filters ? { filters } : {}),
        ...(params.cursor ? { cursor: params.cursor } : {}),
      };
      return postSearch(mode, base, token);
    },
  });

  return {
    data: query.data,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    isIdle: !enabled,
  };
}
